import { useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Button,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useStudentProfile } from '../../hooks/useStudentProfile';
import StudentLayout from '../../components/student/StudentLayout';
import { useGetMarksheetBySemester } from '../../hooks/useGetMarksheetBySemester';
import { useMemo } from 'react';

const StudentCertificatePage = () => {
  const navigate = useNavigate();
  const { data: profile, isLoading } = useStudentProfile();
  const certificateRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Generate 6-digit serial number from registrationNo
  const getSerialNo = (registrationNo: string): string => {
    if (!registrationNo) return '000000';
    
    // Remove any non-digit characters
    const digits = registrationNo.replace(/\D/g, '');
    
    if (digits.length === 0) return '000000';
    
    // Take last 6 digits, or pad with zeros if shorter
    if (digits.length >= 6) {
      return digits.slice(-6);
    } else {
      // Pad with zeros to make it 6 digits
      return digits.padStart(6, '0');
    }
  };

  const serialNo = profile ? getSerialNo(profile.registrationNo) : '000000';

  // Fetch marksheet to calculate percentage and division
  const semestersWithMarksheet: string[] = profile?.whichSemesterMarksheetIsGenerated || [];
  const yearsWithMarksheet: string[] = (profile as any)?.whichYearMarksheetIsGenerated || [];
  const isYearBased = yearsWithMarksheet.length > 0;
  const terms = isYearBased ? yearsWithMarksheet : semestersWithMarksheet;
  const firstTerm = terms.length > 0 ? terms[0] : '1';
  
  const { data: marksheet } = useGetMarksheetBySemester(
    profile?._id || '',
    isYearBased ? '' : firstTerm,
    isYearBased ? firstTerm : '',
    !!profile?._id && terms.length > 0
  );

  // Calculate percentage and division from marksheet
  const { division } = useMemo(() => {
    if (!marksheet?.subjects || marksheet.subjects.length === 0) {
      return { division: 'N/A' };
    }

    const totalTotal = marksheet.subjects.reduce((sum, subject) => sum + (subject.total || 0), 0);
    const totalMaxMarks = marksheet.subjects.reduce((sum, subject) => sum + (subject.maxMarks || 0), 0);
    const calculatedPercentage = totalMaxMarks > 0 ? (totalTotal / totalMaxMarks) * 100 : 0;
    
    let calculatedDivision = 'Fail';
    if (calculatedPercentage >= 70 && calculatedPercentage <= 100) {
      calculatedDivision = `First`;
    } else if (calculatedPercentage >= 60 && calculatedPercentage < 70) {
      calculatedDivision = `Second`;
    } else if (calculatedPercentage >= 50 && calculatedPercentage < 60) {
      calculatedDivision = 'Third';
    } else {
      calculatedDivision = 'Fail';
    }

    return {
      division: calculatedDivision
    };
  }, [marksheet]);

  const handleMenuItemClick = (item: string) => {
    switch (item) {
      case 'profile':
        navigate('/student-dashboard');
        break;
      case 'admitCard':
        navigate('/student/admit-card');
        break;
      case 'marksheet':
        navigate('/student/marksheet');
        break;
      case 'certificate':
        navigate('/student/certificate');
        break;
      case 'migration':
        navigate('/student/migration');
        break;
      default:
        break;
    }
  };

  if (isLoading) {
    return (
      <StudentLayout activeMenuItem="certificate" pageTitle="Certificate" onMenuItemClick={handleMenuItemClick}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  if (!profile?.isCertificateApproved) {
    return (
      <StudentLayout activeMenuItem="certificate" pageTitle="Certificate" onMenuItemClick={handleMenuItemClick}>
        <Box sx={{ p: 3 }}>
          <Alert severity="info">Your certificate is not yet approved. Please contact your center.</Alert>
        </Box>
      </StudentLayout>
    );
  }

  const handleDownload = async () => {
    if (certificateRef.current && profile) {
      setIsDownloading(true);
      try {
        // Wait for all images to load before capturing
        const images = certificateRef.current.querySelectorAll('img');
        await Promise.all(
          Array.from(images).map((img) => {
            return new Promise((resolve) => {
              if (img.complete && img.naturalWidth > 0) {
                resolve(true);
              } else {
                img.onload = () => resolve(true);
                img.onerror = () => resolve(true); // Continue even if image fails to load
              }
            });
          })
        );

        // Additional wait to ensure everything is rendered
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const width = certificateRef.current.scrollWidth;
        const height = certificateRef.current.scrollHeight;
        
        // Use higher scale for better quality (4x for 300 DPI equivalent)
        // 300 DPI = 300 pixels per inch, so for a ~11 inch width, we need ~3300px
        // Scale of 4 gives us 4x the resolution which is good for print quality
        const scale = 4;
        const dpi = 300;
        
        const canvas = await html2canvas(certificateRef.current, {
          backgroundColor: '#ffffff',
          scale: scale,
          useCORS: true,
          allowTaint: true,
          logging: false,
          windowWidth: width,
          windowHeight: height,
          removeContainer: false,
          imageTimeout: 15000,
          onclone: (clonedDoc) => {
            // Ensure all fonts are loaded
            const clonedWindow = clonedDoc.defaultView;
            if (clonedWindow) {
              clonedWindow.document.fonts.ready;
            }
            // Ensure SVG images are rendered at high resolution
            const clonedImages = clonedDoc.querySelectorAll('img');
            clonedImages.forEach((img) => {
              if (img.src.endsWith('.svg')) {
                // Force high resolution rendering for SVG
                (img as HTMLImageElement).style.imageRendering = 'auto';
              }
            });
          },
        });
        
        // Use high-quality JPEG instead of PNG for much smaller file size
        // Quality 0.95 provides excellent quality with significant size reduction
        // This should reduce file size from ~80MB to ~2-5MB while maintaining visual quality
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        
        // Convert original element dimensions to mm for PDF at target DPI
        // The canvas is scaled, but we want PDF dimensions based on original size at 300 DPI
        // Formula: pixels / DPI * 25.4mm per inch
        const pdfWidth = (width / dpi) * 25.4;
        const pdfHeight = (height / dpi) * 25.4;
        
        // Create PDF with proper dimensions in mm
        const pdf = new jsPDF({
          orientation: width > height ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [pdfWidth, pdfHeight],
          compress: true,
        });
        
        // Add high-resolution image - JPEG compression is already applied in toDataURL
        // Use 'FAST' compression for PDF structure (doesn't affect image quality)
        pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
        
        const fileName = `certificate-${profile.registrationNo}-${profile.candidateName.replace(/\s+/g, '-')}.pdf`;
        pdf.save(fileName);
      } catch (error) {
        console.error('Error generating certificate PDF:', error);
        alert('Error generating certificate PDF. Please try again.');
      } finally {
        setIsDownloading(false);
      }
    }
  };

  return (
    <StudentLayout activeMenuItem="certificate" pageTitle="Certificate" onMenuItemClick={handleMenuItemClick}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownload}
            disabled={isDownloading}
            sx={{
              backgroundColor: '#10b981',
              '&:hover': { backgroundColor: '#059669' },
            }}
          >
            {isDownloading ? 'Generating PDF...' : 'Download Certificate (PDF)'}
          </Button>
        </Box>

        <Paper 
          ref={certificateRef}
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            minHeight: '800px', 
            width: { xs: '100%', md: '1056px' },
            height: { xs: 'auto', md: '1494px' },
            aspectRatio: { md: '1056/1494' },
            display: 'flex', 
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
            margin: '0 auto',
          }}
        >
          {/* SVG Background */}
          <Box
            component="img"
            src="/mivps-certificate.svg"
            alt="Certificate Template"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: 'auto',
              zIndex: 0,
              objectFit: 'contain',
            }}
          />
          
          {/* Student Details Overlay - Adjust positions based on actual SVG template */}
          <Box sx={{ position: 'relative', height: '100%', zIndex: 1 }}>
            {/* Serial No in top left corner */}
            <Box sx={{ position: 'absolute', left: '212px', top: '110px' }}>
              <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.45rem' }}>
                {serialNo}
              </Typography>
            </Box>
            
            {/* Candidate Name - Centered */}
            <Box sx={{ 
              position: 'absolute', 
              left: '300px', 
              right: '300px',
              top: '620px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="body2" sx={{ 
                fontWeight: 'bold', 
                fontSize: '1.8rem', 
                fontFamily: '"Dancing Script", cursive',
                textAlign: 'center',
                width: '100%'
              }}>
                {profile.candidateName}
              </Typography>
            </Box>

            {/* Father Name - Centered */}
            <Box sx={{ 
              position: 'absolute', 
              left: '300px', 
              right: '300px',
              top: '735px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="body2" sx={{ 
                fontWeight: 'bold', 
                fontSize: '1.8rem', 
                fontFamily: '"Dancing Script", cursive',
                textAlign: 'center',
                width: '100%'
              }}>
                {profile.fatherName}
              </Typography>
            </Box>

            <Box sx={{ position: 'absolute', left: '582px', top: '791px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '2rem', fontFamily: '"Dancing Script", cursive' }}>
                {profile.registrationNo}
              </Typography>
            </Box>
            
            <Box sx={{ position: 'absolute', left: '275px', top: '940px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '2.5rem', fontFamily: '"Dancing Script", cursive' }}>
                {typeof (profile as any).course === 'object' && (profile as any).course?.name 
                  ? (profile as any).course.name 
                  : (profile.course as string)}
              </Typography>
            </Box>
            
            {/* Division */}
            <Box sx={{ position: 'absolute', left: '445px', top: '1026px' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '2.2rem', fontFamily: '"Dancing Script", cursive' }}>
                {division}
              </Typography>
            </Box>
            
            {/* Place - Hardcoded as New Delhi */}
            <Box sx={{ position: 'absolute', left: '238px', top: '1270px' }}>
              <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '1.5rem', fontFamily: 'Poppins' }}>
                New Delhi
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </StudentLayout>
  );
};

export default StudentCertificatePage;
